import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import styled, { createGlobalStyle } from 'styled-components'
import Header from '@/components/shardLibrary/Header'
import Footer from '@/components/shardLibrary/Footer'
import Shards from '@/components/shardLibrary/Shards'
import { getUserCollections, getUserShards } from '@/utils/api'
import { Container, Div } from '@/styled/common/loading'
import useResizeObserver from 'use-resize-observer/polyfilled'
import debounce from 'lodash/debounce'
import ListIcon from '@/components/icons/ListIcon'
import GridIcon from '@/components/icons/GridIcon'
import { SHARD_WIDTH, SHARD_HEIGHT, CONDENSED_SHARD_HEIGHT, SHARD_GRID_GAP } from '@/config/settings'
import { withScreenInfo } from '@/components/withScreenInfo'
import LoadingAnimation from '@/components/LoadingAnimation'

const GlobalStyle = createGlobalStyle`
  body { font-family: proxima nova !important; }
`

const Page = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0px;
  top: 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  flex-direction: column;
  padding: min(30px, max(10px, min(2vw, 2vh)));
  background-color: var(--ggBackground);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &, * { box-sizing: border-box; }
`

const FilterBar = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
  flex-grow: 0;
  margin-top: 31px;
  margin-bottom: 21px;
`

const Button = styled.div<{ active?: boolean }>`
  width: 35px;
  height: 33px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #232836;
  border: 1px solid #3E455A;
  box-sizing: border-box;
  border-radius: 5px;
  color: white;
  margin-right: 5px;
  cursor: pointer;
  padding: 6px;
  ${(props) => props.active && `
    background: linear-gradient(0deg, rgba(73, 230, 155, 0.1), rgba(73, 230, 155, 0.1)), #232836;
    border: 1px solid #49E69B;
    color: #49E69B;
  `}
`

const Collection = (props: any) => {
  const { collectionId } = Route.useParams()
  const { source } = Route.useSearch()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(true)
  const [shards, setShards] = useState<any[]>([])
  const [shardCount, setShardCount] = useState(0)
  const [filterMethod, setFilterMethod] = useState('grid')
  const [collection, setCollection] = useState<any>(null)
  const mostRecentDimensions = useRef<{ width?: number; height?: number }>({ width: undefined, height: undefined })
  const forceListView = useMemo(() => props?.screen?.height < 485, [props?.screen?.height])

  const resizeOfContent = useMemo(() =>
    debounce(({ width, height }: { width?: number; height?: number }) => {
      if (width && height) mostRecentDimensions.current = { width, height }
      width = width || mostRecentDimensions.current.width
      height = height || mostRecentDimensions.current.height
      if (!width || !height) return

      const rowHeight = filterMethod === 'list' || forceListView ? CONDENSED_SHARD_HEIGHT : SHARD_HEIGHT
      const rows = parseInt(String(height / (rowHeight + (SHARD_GRID_GAP * (Math.round(height / rowHeight) - 1) / Math.round(height / rowHeight)))))
      const columns = parseInt(String(width / (SHARD_WIDTH + (SHARD_GRID_GAP * (parseInt(String(width / SHARD_WIDTH)) - 1) / parseInt(String(width / SHARD_WIDTH))))))
      const newPageSize = Math.round(columns * rows) || 1
      if (newPageSize !== pageSize) setPageSize(newPageSize)
    }, 250),
  [setPageSize, filterMethod, forceListView, pageSize, mostRecentDimensions])

  const { ref, width, height } = useResizeObserver({ onResize: resizeOfContent })

  useEffect(() => {
    /*if (ref.current) {
      resizeOfContent({ width: ref.current.getBoundingClientRect().width, height: ref.current.getBoundingClientRect().height })
    } else {*/
      resizeOfContent({ width, height })
    // }
  }, [height, width, filterMethod])

  const totalPages = useMemo(() => {
    if (!shardCount) return 1
    return Math.ceil(shardCount / pageSize)
  }, [pageSize, shardCount])

  const shardOffset = useMemo(() => page ? page * pageSize : 0, [page, pageSize])

  useEffect(() => {
    getUserCollections(parseInt(collectionId) === 0 ? {
      collection__id__isnull: true
    } : {id: collectionId}).then((response) => {
      setCollection(response.data?.results.at(0))
    })
  }, [collectionId])

  useEffect(() => {

    const params: Record<string, any> = {
      limit:shardOffset < 0 ? pageSize + shardOffset : pageSize,
      is_variant: false,
      offset: shardOffset,
      order_by: '-updated_at'
    }
    if (parseInt(collectionId) === 0) {
      params.collection__id__isnull = true
    } else if (collectionId) {
      params.collection__id = collectionId
    }

    getUserShards(params).then(response => {
      let shardResults = response.data?.results
      if (shardOffset < 0) {
        shardResults = shardResults.slice(0, pageSize)
      }
      setShards([...shardResults])
      setShardCount(response.data?.count)
      setLoading(false)
    })
  }, [collectionId, pageSize, shardOffset])

  const setFilterMethodMiddleware = useCallback((type: string) => {
    setFilterMethod(type);
    setPage(0)
  }, [])

  return (
    <>
      <GlobalStyle />
      <Page>
        {loading ? (
          <Container className={props.className || 'Loading'} style={props.style} fixed={props.fixed}>
            <LoadingAnimation />
            {props.message && <Div>{props.message}</Div>}
          </Container>
        ) : (
          <>
            <Header collectionName={parseInt(collectionId) ? collection?.name : 'Not In Collection'} source={source} />
            {!forceListView && (
              <FilterBar>
                <Button active={filterMethod === 'grid'} onClick={() => setFilterMethodMiddleware('grid')}>
                  <div style={{width:'13px',height:'13px',display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <GridIcon />
                  </div>
                </Button>
                <Button active={filterMethod === 'list'} onClick={() => setFilterMethodMiddleware('list')}>
                  <div style={{width:"14px",height:"14px",display:"flex",alignItems:'center',justifyContent:'center'}}>
                    <ListIcon />
                  </div>
                </Button>
              </FilterBar>
            )}
            <Shards
              contentRef={ref}
              shards={shards || []}
              rowHeight={(filterMethod === 'grid' && !forceListView) ? SHARD_HEIGHT : CONDENSED_SHARD_HEIGHT}
              filterMethod={forceListView ? 'list' : filterMethod}
            />
            {totalPages > 1 && <Footer totalPages={totalPages} currentPage={page} setPage={setPage} />}
          </>
        )}
      </Page>
    </>
  )
}

export const Route = createFileRoute('/_auth/shardlibrary/$collectionId/')({
  validateSearch: (search?: Record<string, string | undefined>) => ({
    source: search?.source,
  }),
  component: withScreenInfo(Collection),
})
